import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { combineComparers, distinct, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type {
  ContactDetails,
  Investor,
  UpdateInvestorCommunicationMatrixRequest,
} from "../../../../../api/types/fundStructureTypes";
import CommunicationMatrix from "../../common/CommunicationMatrix";
import { ContactMatrixInvestorGroupCell, MatrixFundRowCell } from "../../common/matrixGridComponents";
import type { CommunicationMatrixModel } from "../../common/matrixHelper";
import {
  cloneContactModelWithoutChildren,
  createContactModelsForAddedInvestors,
  getContactChangesWhereInvitationWouldBeSent,
  getContactMatrixTreeDataPath,
  getGroupNodeModel,
} from "../../common/matrixHelper";
import AssignInvestorsDialog from "./AssignInvestorsDialog";

interface Props {
  categories: Category[];
  contactDetails: ContactDetails;
  investors: Investor[];
  onUpdateContactDetails: () => void;
}

const sortModelFunc = combineComparers<CommunicationMatrixModel>(
  stringComparerBy((m) => m.investorName),
  stringComparerBy((m) => m.fundName)
);

const ContactCommunicationMatrix = ({ categories, contactDetails, investors, onUpdateContactDetails }: Props) => {
  const originalModels = useMemo(() => {
    const investorsThatContactHasPermissionsFor = investors.filter((investor) =>
      contactDetails.permissions.some((cp) => investor.funds.map((f) => f.fundInvestorId).includes(cp.fundInvestorId))
    );
    return createContactModelsForAddedInvestors(investorsThatContactHasPermissionsFor, contactDetails).sort((a, b) =>
      a.investorName.localeCompare(b.investorName)
    );
  }, [contactDetails, investors]);

  const [initialModels, setInitialModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [models, setModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [showAssignInvestorsDialog, setShowAssignInvestorsDialog] = useState(false);

  const excludeInvestorIds = useMemo(
    () => distinct(models.map((model) => model.investorId).filter((id): id is string => id !== undefined)),
    [models]
  );

  const getChangesWhereInvitationWouldBeSent = (
    initialModels: CommunicationMatrixModel[],
    modifiedModels: CommunicationMatrixModel[]
  ) => getContactChangesWhereInvitationWouldBeSent([contactDetails.contact], initialModels, modifiedModels);

  const handleAddInvestorClick = () => {
    setShowAssignInvestorsDialog(true);
  };

  const handleInvestorsSelected = (selectedInvestors: Investor[]) => {
    setModels((currentModels) => [
      ...currentModels,
      ...createContactModelsForAddedInvestors(selectedInvestors, contactDetails),
    ]);

    setShowAssignInvestorsDialog(false);
  };

  const handleUpdateCommunicationMatrix = async (payload: UpdateInvestorCommunicationMatrixRequest) => {
    const response = await adminApi.updateInvestorCommunicationMatrix(payload);
    if (response.success) {
      onUpdateContactDetails();
    }
    return response;
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      {!contactDetails.contact.email && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" href={`../${contactDetails.contact.id}/main`} target={"_blank"}>
              Add Email
            </Button>
          }
        >
          To start assigning accesses, please provide an email address for this contact.
        </Alert>
      )}
      <CommunicationMatrix
        entityType="Contact"
        noItemsMessage={"This contact has not been added to any investor."}
        renderGroupCellElement={(value, node) => (
          <ContactMatrixInvestorGroupCell
            value={value as string}
            investorId={getGroupNodeModel(models, node)?.investorId}
          />
        )}
        renderGridToolbarComponent={(isLoading: boolean) => (
          <Button
            startIcon={<AddRoundedIcon />}
            disabled={isLoading || !contactDetails.contact.email}
            onClick={handleAddInvestorClick}
            variant="contained"
            color="primary"
          >
            Add Investor
          </Button>
        )}
        categories={categories}
        initialModels={initialModels}
        setInitialModels={setInitialModels}
        models={models}
        setModels={setModels}
        getChangesWhereInvitationWouldBeSent={getChangesWhereInvitationWouldBeSent}
        sortModelFunc={sortModelFunc}
        getTreeDataPath={getContactMatrixTreeDataPath}
        cloneModelWithoutChildren={cloneContactModelWithoutChildren}
        renderGroupedRowCell={(model: CommunicationMatrixModel) => <MatrixFundRowCell model={model} />}
        childRowActionsDisabled
        updateInvestorCommunicationMatrix={handleUpdateCommunicationMatrix}
        excludedColumns={["isPrimary"]}
      />
      <AssignInvestorsDialog
        open={showAssignInvestorsDialog}
        onClose={() => setShowAssignInvestorsDialog(false)}
        title={`Add investor(s) to ${contactDetails.contact.name}`}
        description="Select the investor(s) you wish to associate with this contact."
        excludeInvestorIds={excludeInvestorIds}
        onSelected={handleInvestorsSelected}
      />
    </Stack>
  );
};

export default ContactCommunicationMatrix;
